import useUpdateEffect from '@restart/hooks/useUpdateEffect';
import classNames from 'classnames/bind';
import React, { useState, useRef } from 'react';

import styles from './index.module.scss';

import ConfirmSlider from '@/components/ui/ConfirmSlider';
import Spinner from '@/components/ui/Spinner';
import useActionCommand from '@/hooks/command/mavlink/useActionCommand';
import { NotifierService as notifier } from '@/libs/Notifier';
import { executeMavLinkCommand } from '@/utils/CommandUtils';
import { sleep } from '@/utils/Sleep';
import { getCSSVariable } from '@/utils/theme';

const cx = classNames.bind(styles);

// 상수 정의
const MOTOR_NUMBERS = [1, 2, 3, 4];
const MOTOR_PWM = 1100;
const TIMEOUT = 3;

const Panel = ({ data: robots }) => {
  const [isProcessing, setProcessing] = useState(false);
  const [currentTest, setCurrentTest] = useState();
  const testTarget = useRef();

  const actionCommand = useActionCommand();

  //#region Process Functions
  const process = async (robot) => {
    try {
      // 전체 테스트
      if (testTarget.current === 'all') {
        // 모터 1번
        setCurrentTest(1);
        await processMotor(robot, 1);

        // 모터 2번
        setCurrentTest(2);
        await processMotor(robot, 2);

        // 모터 3번
        setCurrentTest(3);
        await processMotor(robot, 3);

        // 모터 4번
        setCurrentTest(4);
        await processMotor(robot, 4);
      }
      // 개별 테스트
      else {
        setCurrentTest(testTarget.current);
        await processMotor(robot, testTarget.current);
      }

      onSuccess(robot);
    } catch (error) {
      onFailure(robot);
    }
  };

  const processMotor = async (robot, motorNumber) => {
    await executeMavLinkCommand(robot, actionCommand.motorTest(motorNumber, 1, MOTOR_PWM, TIMEOUT));
    await sleep(TIMEOUT * 1000 + 1000); // 모터 테스트 실행 후 대기
  };
  //#endregion

  //#region Result Handlers
  const onSuccess = (robot) => {
    testTarget.current = null;
    setCurrentTest();

    notifier.success(`Inspection completed successfully on ${robot.name}.`);
    setProcessing(false);
  };

  const onFailure = (robot) => {
    testTarget.current = null;
    setCurrentTest();

    notifier.error(`Inspection failed on ${robot.name}.`);
    setProcessing(false);
  };
  //#endregion

  useUpdateEffect(() => {
    if (!isProcessing) return;

    robots.forEach((robot) => process(robot));
  }, [isProcessing]);

  const doStartAll = () => {
    testTarget.current = 'all';
    setProcessing(true);
  };

  const doStartSingle = (e) => {
    testTarget.current = Number(e.currentTarget.dataset.motor);
    setProcessing(true);
  };

  const getActive = (number) => {
    return currentTest === number;
  };

  return (
    <div className={cx('container')}>
      <div className={cx('figure')}>
        <img src={require('./inspection-body.png')} alt="" className={cx('body')} />
        {/* Motor #1 */}
        <div className={cx('motor', 'motor1')}>
          <img
            src={require('./inspection-propeller.png')}
            alt=""
            className={cx(['propeller', 'reverse', { active: getActive(MOTOR_NUMBERS[0]) }])}
          />
          <div className={cx('info')}>
            <div className={cx('wrapper')}>
              <div className={cx('name')}>Motor #1</div>
              <button data-motor={1} data-type="motor" onClick={doStartSingle} disabled={isProcessing}>
                {!getActive(MOTOR_NUMBERS[0]) && 'Start'}
                {getActive(MOTOR_NUMBERS[0]) && <Spinner size={14} />}
              </button>
            </div>
            <svg width="36" height="40">
              <line
                x1="36"
                y1="0"
                x2="0"
                y2="40"
                stroke={getCSSVariable('--line-color')}
                strokeWidth="1"
                strokeDasharray="3,3"
              />
            </svg>
          </div>
        </div>
        {/* Motor #2 */}
        <div className={cx('motor', 'motor2')}>
          <img
            src={require('./inspection-propeller.png')}
            alt=""
            className={cx(['propeller', { active: getActive(MOTOR_NUMBERS[1]) }])}
          />
          <div className={cx('info')}>
            <svg width="36" height="40">
              <line
                x1="0"
                y1="0"
                x2="36"
                y2="40"
                stroke={getCSSVariable('--line-color')}
                strokeWidth="1"
                strokeDasharray="3,3"
              />
            </svg>
            <div className={cx('wrapper')}>
              <div className={cx('name')}>Motor #2</div>
              <button data-motor={2} data-type="motor" onClick={doStartSingle} disabled={isProcessing}>
                {!getActive(MOTOR_NUMBERS[1]) && 'Start'}
                {getActive(MOTOR_NUMBERS[1]) && <Spinner size={14} />}
              </button>
            </div>
          </div>
        </div>
        {/* Motor #3 */}
        <div className={cx('motor', 'motor3')}>
          <img
            src={require('./inspection-propeller.png')}
            alt=""
            className={cx(['propeller', 'reverse', { active: getActive(MOTOR_NUMBERS[2]) }])}
          />
          <div className={cx('info')}>
            <svg width="72" height="40">
              <line
                x1="72"
                y1="0"
                x2="36"
                y2="40"
                stroke={getCSSVariable('--line-color')}
                strokeWidth="1"
                strokeDasharray="3,3"
              />
            </svg>
            <div className={cx('wrapper')}>
              <div className={cx('name')}>Motor #3</div>
              <button data-motor={3} data-type="motor" onClick={doStartSingle} disabled={isProcessing}>
                {!getActive(MOTOR_NUMBERS[2]) && 'Start'}
                {getActive(MOTOR_NUMBERS[2]) && <Spinner size={14} />}
              </button>
            </div>
          </div>
        </div>
        {/* Motor #4 */}
        <div className={cx('motor', 'motor4')}>
          <img
            src={require('./inspection-propeller.png')}
            alt=""
            className={cx(['propeller', { active: getActive(MOTOR_NUMBERS[3]) }])}
          />
          <div className={cx('info')}>
            <div className={cx('wrapper')}>
              <div className={cx('name')}>Motor #4</div>
              <button data-motor={4} data-type="motor" onClick={doStartSingle} disabled={isProcessing}>
                {!getActive(MOTOR_NUMBERS[3]) && 'Start'}
                {getActive(MOTOR_NUMBERS[3]) && <Spinner size={14} />}
              </button>
            </div>
            <svg width="72" height="40">
              <line
                x1="36"
                y1="0"
                x2="72"
                y2="40"
                stroke={getCSSVariable('--line-color')}
                strokeWidth="1"
                strokeDasharray="3,3"
              />
            </svg>
          </div>
        </div>
      </div>
      <ConfirmSlider onConfirm={doStartAll} isProcessing={isProcessing} />
    </div>
  );
};

export default Panel;
